import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bus, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/site/Logo";

const todaySyd = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
const longDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const SessionBlock = ({ s }) => {
  const lifts = s.people.filter((p) => p.lift);
  return (
    <section data-testid={`runsheet-session-${s.id}`} className="break-inside-avoid border-t-2 border-ink pt-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-3xl">{s.course_title}</h2>
        <p className="font-mono text-sm">{s.start_time}{s.end_time ? `–${s.end_time}` : ""}{s.location ? ` · ${s.location}` : ""}</p>
      </div>
      <p className="mt-1 text-sm text-ink/70">{s.people.length} attending · {lifts.length} need a lift</p>
      {lifts.length > 0 && (
        <table className="mt-4 w-full text-left text-sm" data-testid={`runsheet-lifts-${s.id}`}>
          <thead><tr className="border-b border-ink/30"><th className="py-2"><Bus className="inline h-4 w-4" /> Lift for</th><th>Name</th><th>Pick-up suburb</th><th>Notes</th><th>Status</th><th className="w-10">✓</th></tr></thead>
          <tbody>
            {lifts.map((p) => (
              <tr key={p.email} className="border-b border-ink/10 align-top">
                <td className="py-2">{p.transport_for}</td><td className="font-semibold">{p.name}</td><td>{p.pickup_suburb}</td>
                <td>{p.transport_notes || "—"}</td><td className="font-mono text-xs uppercase">{(p.transport_status || "requested").replace("_", " ")}</td><td><span className="inline-block h-4 w-4 border border-ink" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="eyebrow mt-5 text-ink/70">Attendance</p>
      <ul className="mt-2 grid gap-x-8 sm:grid-cols-2">
        {s.people.map((p) => (
          <li key={p.email} className="flex items-center gap-3 border-b border-ink/10 py-1.5 text-sm"><span className="inline-block h-4 w-4 shrink-0 border border-ink" />{p.name}{p.lift && <Bus className="h-3.5 w-3.5 text-ochre" />}</li>
        ))}
        {!s.people.length && <li className="py-2 text-sm text-ink/60">No one enrolled yet.</li>}
      </ul>
    </section>
  );
};

export default function RunSheet() {
  const [date, setDate] = useState(todaySyd());
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/run-sheet", { params: { date } }).then((r) => setData(r.data)).catch(() => setData({ sessions: [] }));
  }, [date]);
  const sessions = Array.isArray(data?.sessions) ? data.sessions : [];

  return (
    <main className="min-h-screen bg-sand px-5 pb-24 pt-32 sm:px-10 print:bg-white print:p-0">
      <div className="mx-auto max-w-[1000px]">
        <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
          <Link to="/admin" data-testid="runsheet-back" className="flex items-center gap-2 text-sm hover:text-ochre"><ArrowLeft className="h-4 w-4" /> Admin</Link>
          <div className="flex items-center gap-3">
            <input type="date" data-testid="runsheet-date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl border border-ink/20 bg-white/70 px-3 py-2 text-sm" />
            <button data-testid="runsheet-print" onClick={() => window.print()} className="btn-ochre py-2.5"><Printer className="h-4 w-4" /> Print</button>
          </div>
        </div>
        <header className="mt-10 flex items-center gap-4 print:mt-0">
          <LogoMark className="h-12 w-12" />
          <div>
            <p className="eyebrow text-ochre">GAC · Daily run sheet</p>
            <h1 data-testid="runsheet-title" className="font-display text-4xl sm:text-5xl">{longDate(date)}</h1>
          </div>
        </header>
        <p className="mt-3 text-sm text-ink/70">{sessions.length} session{sessions.length === 1 ? "" : "s"} · {(() => { const n = sessions.reduce((t, s) => t + s.people.filter((p) => p.lift).length, 0); return `${n} lift${n === 1 ? "" : "s"}`; })()}</p>
        <div className="mt-8 space-y-10">
          {sessions.map((s) => <SessionBlock key={s.id} s={s} />)}
          {data && !sessions.length && <p data-testid="runsheet-empty" className="rounded-2xl border border-dashed border-ink/25 p-8 text-ink/70">No sessions scheduled on this day. Add sessions in the admin Sessions tab.</p>}
        </div>
        <p className="mt-12 hidden text-xs text-ink/60 print:block">Confidential — contains personal information. Please store securely and shred after use.</p>
      </div>
    </main>
  );
}
